import { OrientedVolume } from '@data/buildingProjects/DesignSchemes/SingleBuilding/OrientedVolume';
import { alphaShapeHullLoops, simplifyCollinear } from '@data/geometry/ConcaveHull';
import FVector from '@data/geometry/FVector';
import type { OrientedVolumeDesignState } from '@visualizer/types';
import { LayerKind } from '@visualizer/types';
import * as THREE from 'three';
import { Brush, Evaluator, HOLLOW_INTERSECTION, INTERSECTION, SUBTRACTION } from 'three-bvh-csg';
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import Grid from '../../../Geodata/grid';
import { DesignSchemeRenderer } from '../DesignSchemeRenderer';
import { generatePointsCloud, removeDuplicatePoints } from '../DesignSchemeUtils';
import { getViewer } from 'store/viewerStore';
import { tagDesignSchemeSurfaceMaterial } from '../DesignSchemeSurfaceMaterialHelper';

export class OrientedVolumeRenderer extends DesignSchemeRenderer {
    private orientedVolume: OrientedVolume;
    declare protected config: OrientedVolumeDesignState;

    constructor(orientedVolumeScheme: OrientedVolume, config: OrientedVolumeDesignState) {
        super(config);
        this.orientedVolume = orientedVolumeScheme;
    }

    public updateConfig(newConfig: Partial<OrientedVolumeDesignState>) {
        this.config = this.cloneConfig({ ...this.config, ...newConfig } as OrientedVolumeDesignState);
    }

    async generateMesh() {
        // 1. Get config & sensors:
        const step = this.config.step.value;
        const transparent = this.config.transparent ?? true;
        const useConcave = this.config.concave ?? false;
        const concaveAlpha = this.config.concaveAlpha?.value ?? Math.max(Grid.gridRes * 1.5, step * 1.5);

        const sensors = this.orientedVolume.getSensorDatasFor(LayerKind.MAIN);

        ///////////////////////
        // Utility functions //
        ///////////////////////

        function findFurthestPointAlongRay(geometry: THREE.BufferGeometry, direction: THREE.Vector3, startPosition: THREE.Vector3) {
            let furthestPoint = { distance: -Infinity, point: new THREE.Vector3() };
            const vertices = geometry.attributes.position.array as Float32Array;

            for (let i = 0; i < vertices.length; i += 3) {
                const vertex = new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2]);
                const distance = vertex.clone().sub(startPosition).dot(direction);
                if (distance > furthestPoint.distance) {
                    furthestPoint.distance = distance;
                    furthestPoint.point.copy(vertex);
                }
            }

            return furthestPoint;
        }

        function mergePlanesIntoSingleMesh(group: THREE.Group) {
            const geometries: THREE.BufferGeometry[] = [];
            group.children.forEach((child) => {
                if (child instanceof THREE.Mesh && child.geometry instanceof THREE.BufferGeometry) {
                    geometries.push(child.geometry);
                }
            });
            if (geometries.length === 0) return null;
            const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries);
            return new THREE.Mesh(mergedGeometry, (group.children[0] as THREE.Mesh).material);
        }

        function csgOperation(mesh1: THREE.Mesh, mesh2: THREE.Mesh, operation = HOLLOW_INTERSECTION) {
            const toBrush = (mesh: THREE.Mesh) => {
                const brush = new Brush(mesh.geometry);
                brush.position.copy(mesh.position);
                brush.rotation.copy(mesh.rotation);
                brush.scale.copy(mesh.scale);
                brush.updateMatrixWorld();
                brush.prepareGeometry();
                return brush;
            };

            const setMaterial = (brush: Brush, opacity: number, color: number) => {
                const mat = brush.material as THREE.MeshStandardMaterial;

                mat.opacity = opacity;
                mat.transparent = true;
                mat.depthWrite = false;
                mat.polygonOffset = true;
                mat.polygonOffsetFactor = 0.2;
                mat.polygonOffsetUnits = 0.2;
                mat.side = THREE.DoubleSide;
                mat.premultipliedAlpha = true;
                mat.roughness = 0.25;
                mat.color.set(color);
            };

            const brush1 = toBrush(mesh1);
            const brush2 = toBrush(mesh2);
            setMaterial(brush1, 0.35, 0xe91e63);
            setMaterial(brush2, 0.15, 0x808080);

            const evaluator = new Evaluator();
            evaluator.attributes = ['position', 'normal'];
            const result = evaluator.evaluate(brush1, brush2, operation);
            return result.geometry;
        }

        function generateThickPlaneAlongDirection(mesh: THREE.Mesh, thickness: number, direction: THREE.Vector3) {
            const pts: THREE.Vector3[] = [];
            const pos = mesh.geometry.attributes.position.array as Float32Array;
            for (let i = 0; i < pos.length; i += 3) {
                pts.push(new THREE.Vector3(pos[i], pos[i + 1], pos[i + 2]));
            }
            const dir = direction.clone().normalize();
            const pts1 = pts.map((p) => p.clone().add(dir.clone().multiplyScalar(thickness / 2)));
            const pts2 = pts.map((p) => p.clone().add(dir.clone().multiplyScalar(-thickness / 2)));
            const merged = [...pts1, ...pts2];
            return new ConvexGeometry(merged);
        }

        // Concave volume (2.5D) needed to then keep only the oriented volume portion inside of it:
        function buildConcaveHullExtrusion(pointsCloud: FVector[], minZ: number, maxZ: number, alpha: number, offsetDistance: number) {
            function intersectOffsetLines(
                pointA: THREE.Vector2,
                dirA: THREE.Vector2,
                pointB: THREE.Vector2,
                dirB: THREE.Vector2
            ): THREE.Vector2 | null {
                const denom = dirA.x * dirB.y - dirA.y * dirB.x;
                if (Math.abs(denom) <= 1e-8) return null;

                const delta = pointB.clone().sub(pointA);
                const t = (delta.x * dirB.y - delta.y * dirB.x) / denom;
                return pointA.clone().add(dirA.clone().multiplyScalar(t));
            }

            function offsetLoop(loop: FVector[], distance: number) {
                if (distance <= 0 || loop.length < 3) return loop;

                const offsetLoopPoints: FVector[] = [];
                for (let i = 0; i < loop.length; i++) {
                    const prev = loop[(i - 1 + loop.length) % loop.length];
                    const curr = loop[i];
                    const next = loop[(i + 1) % loop.length];

                    const prevDir = new THREE.Vector2(curr.x - prev.x, curr.y - prev.y).normalize();
                    const nextDir = new THREE.Vector2(next.x - curr.x, next.y - curr.y).normalize();
                    const prevNormal = new THREE.Vector2(prevDir.y, -prevDir.x).multiplyScalar(distance);
                    const nextNormal = new THREE.Vector2(nextDir.y, -nextDir.x).multiplyScalar(distance);

                    const prevPoint = new THREE.Vector2(curr.x, curr.y).add(prevNormal);
                    const nextPoint = new THREE.Vector2(curr.x, curr.y).add(nextNormal);
                    const intersection = intersectOffsetLines(prevPoint, prevDir, nextPoint, nextDir);

                    if (intersection) {
                        offsetLoopPoints.push(new FVector(intersection.x, intersection.y, curr.z));
                    } else {
                        const fallback = prevNormal.clone().add(nextNormal).multiplyScalar(0.5);
                        offsetLoopPoints.push(new FVector(curr.x + fallback.x, curr.y + fallback.y, curr.z));
                    }
                }

                return offsetLoopPoints;
            }

            const uniqueXY = new Map<string, FVector>();

            for (const point of pointsCloud) {
                const key = `${point.x.toFixed(6)},${point.y.toFixed(6)}`;
                if (!uniqueXY.has(key)) uniqueXY.set(key, new FVector(point.x, point.y, minZ));
            }

            const footprintPoints = Array.from(uniqueXY.values());
            if (footprintPoints.length < 3 || maxZ <= minZ) return null;

            const loops = alphaShapeHullLoops(footprintPoints, alpha)
                .map((loop) => simplifyCollinear(loop))
                .map((loop) => offsetLoop(loop, offsetDistance))
                .filter((loop) => loop.length >= 3);

            if (loops.length === 0) return null;

            const baseDepth = maxZ - minZ;
            const extrusionScale = 1.5;
            const expandedDepth = baseDepth * extrusionScale;
            const centerZ = (minZ + maxZ) * 0.5;
            const extrudeMinZ = centerZ - expandedDepth * 0.5;
            const geometries = loops.map((loop) => {
                const shape = new THREE.Shape(loop.map((point) => new THREE.Vector2(point.x, point.y)));
                const geom = new THREE.ExtrudeGeometry(shape, {
                    depth: expandedDepth,
                    bevelEnabled: false,
                    steps: 1,
                });
                geom.translate(0, 0, extrudeMinZ);
                return geom;
            });

            const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries, false) ?? geometries[0];
            const weldedGeometry = BufferGeometryUtils.mergeVertices(mergedGeometry, 1e-5);
            weldedGeometry.computeVertexNormals();
            return weldedGeometry;
        }

        //////////////////
        // Computations //
        //////////////////

        let pointsCloud = generatePointsCloud(sensors);
        removeDuplicatePoints(pointsCloud);
        const points = pointsCloud.map((p) => new THREE.Vector3(p.x, p.y, p.z));
        if (points.length === 0) return;

        // 2. Build the source convex volume used by the oriented slicing pipeline:
        const sourceGeometry = new ConvexGeometry(points);
        const sourceMaterial = new THREE.MeshBasicMaterial({ color: 0x808080, side: THREE.DoubleSide });
        tagDesignSchemeSurfaceMaterial(sourceMaterial, 'opaque');
        const sourceMesh = new THREE.Mesh(sourceGeometry, sourceMaterial);
        sourceMesh.visible = false;
        this.meshGroup.add(sourceMesh);

        const edgeMat = new THREE.LineBasicMaterial({ color: 0x000000 });
        const sourceEdges = new THREE.EdgesGeometry(sourceGeometry);
        const sourceLineMesh = new THREE.LineSegments(sourceEdges, edgeMat);
        sourceLineMesh.visible = false;
        this.meshGroup.add(sourceLineMesh);

        // 2.1 Compute the main dir and the number of slices:
        const bufferGeom = new THREE.BufferGeometry().setFromPoints(points);
        const center = new THREE.Vector3();
        bufferGeom.computeBoundingBox();
        bufferGeom.boundingBox?.getCenter(center);
        const sourceBounds = bufferGeom.boundingBox?.clone();

        // Sum raw vectors from sensors
        let result = new FVector();
        for (const sd of sensors) {
            const v = sd.getVectorialResult();
            if (v) result.add(v);
        }
        result = result.normalize();

        const sunDir = new THREE.Vector3(result.x, result.y, result.z);
        const revDir = sunDir.clone().negate();

        // Find extent along sun direction
        const far = findFurthestPointAlongRay(bufferGeom, sunDir, center);
        const farOpp = findFurthestPointAlongRay(bufferGeom, revDir, center);

        const dist = far.point.distanceTo(farOpp.point);
        const nPlanes = Math.ceil(dist / step);

        // 2.2 Build oriented planes (no thickness):
        const planeGroup = new THREE.Group();
        for (let i = 0; i < nPlanes; i++) {
            if (i > 0 && i % 25 === 0) {
                await this.yieldToUi();
                if (this.shouldAbortGeneration()) return;
            }

            const pos = far.point.clone().add(revDir.clone().multiplyScalar(step * (i + 0.5)));

            const planeGeom = new THREE.PlaneGeometry(300, 300);
            const planeMat = new THREE.MeshBasicMaterial({
                color: 0xffff00,
                side: THREE.DoubleSide,
            });
            const plane = new THREE.Mesh(planeGeom, planeMat);

            plane.position.copy(pos);
            plane.lookAt(pos.clone().add(sunDir));

            planeGroup.add(plane);
        }

        // 2.3 Build the concave volume to then use it for CSG Intersection:
        const concaveHullGeometry =
            useConcave && sourceBounds && sourceBounds.max.z > sourceBounds.min.z
                ? buildConcaveHullExtrusion(pointsCloud, sourceBounds.min.z, sourceBounds.max.z, concaveAlpha, Grid.gridRes * 2)
                : null;

        const concaveHullMesh = concaveHullGeometry
            ? new THREE.Mesh(concaveHullGeometry, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }))
            : null;

        // 3. Generate oriented volume slices and clip them against the concave volume:
        const resultGroup = new THREE.Group();
        for (let i = 0; i < planeGroup.children.length; i++) {
            if (i > 0 && i % 5 === 0) {
                await this.yieldToUi();
                if (this.shouldAbortGeneration()) return;
            }

            const plane = planeGroup.children[i];
            // 3.1 Intersect the current plane (no depth planes) with the source volume to get the slice section.
            const sourceSliceGeometry = csgOperation(plane as THREE.Mesh, sourceMesh, HOLLOW_INTERSECTION);
            if (!sourceSliceGeometry) continue;

            // 3.2 Thicken the slice section into the oriented slab volume.
            const tmpMesh = new THREE.Mesh(sourceSliceGeometry, new THREE.MeshBasicMaterial());
            const thickGeom = generateThickPlaneAlongDirection(tmpMesh, step, sunDir);
            if (thickGeom) {
                let finalGeometry = thickGeom as THREE.BufferGeometry;
                if (concaveHullMesh) {
                    // 3.3 Clip the slab against the concave hull (gives the oriente volume concave shape):
                    const thickMesh = new THREE.Mesh(
                        thickGeom,
                        new THREE.MeshBasicMaterial({
                            color: 0x808080,
                            side: THREE.DoubleSide,
                            polygonOffset: true,
                            polygonOffsetFactor: 1,
                            polygonOffsetUnits: 1,
                        })
                    );
                    const clippedThickGeometry = csgOperation(thickMesh, concaveHullMesh, INTERSECTION);
                    if (!clippedThickGeometry) continue;
                    finalGeometry = clippedThickGeometry;
                }

                const finalMesh = new THREE.Mesh(
                    finalGeometry,
                    new THREE.MeshBasicMaterial({
                        color: 0x808080,
                        side: THREE.DoubleSide,
                        transparent,
                        opacity: transparent ? 0.5 : 1,
                        polygonOffset: true,
                        polygonOffsetFactor: 1,
                        polygonOffsetUnits: 1,
                    })
                );
                tagDesignSchemeSurfaceMaterial(finalMesh.material, 'opaque');
                const finalStrokes = new THREE.LineSegments(
                    new THREE.EdgesGeometry(finalGeometry, 0.1),
                    new THREE.LineBasicMaterial({
                        color: 0x000000,
                        polygonOffset: true,
                        depthTest: true,
                        polygonOffsetFactor: 3,
                        polygonOffsetUnits: 0.1,
                        linewidth: 1,
                    })
                );
                resultGroup.add(finalMesh);
                resultGroup.add(finalStrokes);
            }
        }

        if (this.shouldAbortGeneration()) return;

        this.meshGroup.add(resultGroup);

        // 4. Compute the complementary empty volume from the final slab result.
        const merged = mergePlanesIntoSingleMesh(resultGroup);
        if (merged) {
            const emptyGeom = csgOperation(merged, sourceMesh, SUBTRACTION);
            if (emptyGeom) {
                const emptyMat = new THREE.MeshBasicMaterial({ color: 0x808080, side: THREE.DoubleSide });
                tagDesignSchemeSurfaceMaterial(emptyMat, 'opaque');
                const emptyMesh = new THREE.Mesh(emptyGeom, emptyMat);
                emptyMesh.visible = false;
                this.meshGroup.add(emptyMesh);
                const emptyLineMesh = new THREE.LineSegments(new THREE.EdgesGeometry(emptyGeom), edgeMat);
                emptyLineMesh.visible = false;
                this.meshGroup.add(emptyLineMesh);
            }
        }

        if (this.shouldAbortGeneration()) return;

        // 5. Add the assembled group to the scene.
        const scene: THREE.Scene = getViewer().scene;
        scene.add(this.meshGroup);
    }
}
